/**
 * GridWrapper.jsx
 *
 * ROOT CAUSES FIXED:
 *
 * 1. setActiveCellFormula in the store was clobbering formulaBarValue.
 *    Fixed in spreadsheetStores.js — it now skips the overwrite when isEditing
 *    and the formula starts with '='.
 *
 * 2. handleCellFocused was still running its full navigation logic (setActiveCellAddress,
 *    HF lookup, setActiveCellFormula) even in formula mode. That navigation
 *    is what was replacing the formula bar content. Fixed: return early in formula mode.
 *
 * 3. Drag detection with ranges.length > 0 was wrong — AG Grid always has ranges
 *    even for single clicks. Fixed: use real window mousedown/mouseup tracking.
 *    - Single click: mouse is UP when cellFocused fires → insert single cell ref
 *    - Drag: mouse is DOWN when cellFocused fires → skip (onRangeSelectionChanged handles it)
 *
 * 4. onRangeSelectionChanged fires on every frame during drag. Fixed with 80ms debounce.
 *    Also only acts when mouse is up (drag settled).
 *
 * 5. Reference insertion was broken — kept appending instead of replacing previous ref.
 *    Fixed: track last inserted ref in prevRefRef, replace it in-place with lastIndexOf.
 *
 * 6. Cell editor opening during formula mode was causing issues.
 *    Fixed: suppress cell editor open when in formula mode.
 */

import React, { useRef, useCallback, useMemo, useEffect, memo, forwardRef, useImperativeHandle } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import { parseColumnsToAgDefs }                       from '../../../libraries/spreadsheet/core/metadata/metadataParser';
import validationEngine                               from '../../../libraries/spreadsheet/core/validation/validationEngine';
import formulaWorkerBridge                            from '../../../libraries/spreadsheet/workers/formulaWorkerBridge';
import { buildCellAddress, fieldToColIndex }          from '../../../libraries/spreadsheet/utils/cellAddressParser';
import { useSheetStore, useFormulaStore, useUiStore } from '../../../libraries/spreadsheet/store/spreadsheetStores';
import { normalizeCellRange }                         from './formulaHelpers';

const DEFAULT_COL_DEF = { resizable: true, sortable: true, filter: true, tooltipShowDelay: 400 };
const GRID_OPTIONS = {
  rowBuffer: 20,
  animateRows: true,
  stopEditingWhenCellsLoseFocus: false, // we control this manually
  undoRedoCellEditing: true,
  undoRedoCellEditingLimit: 20,
  pagination: true,
  paginationPageSize: 100,
  rowHeight: 36,
  headerHeight: 38,
};

const GridWrapper = memo(forwardRef(({
  workbookId, sheetMeta, permissions, hfSheetIndex, rowData: propRowData, onCellChanged, onGridReady,
}, ref) => {
  const gridRef  = useRef(null);
  const sheetId  = sheetMeta.sheetId;

  // ── Mouse state tracking ──────────────────────────────────────────────────
  // We track actual OS-level mouse state so we can distinguish:
  //   - Single click: cellFocused fires after mouseup (isMouseDown=false)
  //   - Drag:         cellFocused fires while mousedown (isMouseDown=true) → skip
  const isMouseDownRef  = useRef(false);

  // ── Formula reference tracking ────────────────────────────────────────────
  // The last reference text we inserted, so we can replace it on the next selection
  const prevRefRef      = useRef(null);

  // ── Debounce for range selection ──────────────────────────────────────────
  const rangeDebounceRef = useRef(null);

  // ── Cell editor sync ──────────────────────────────────────────────────────
  // Listener attached to the cell editor input to mirror keystrokes → formula bar
  const editorSyncRef   = useRef(null);

  // ── Stores ───────────────────────────────────────────────────────────────
  const storeRowData   = useSheetStore((s) => s.rowDataMap[sheetId] || []);
  const updateCell     = useSheetStore((s) => s.updateCell);
  const updateComputed = useSheetStore((s) => s.updateComputedCells);
  const setRowErrors   = useSheetStore((s) => s.setRowErrors);

  const setActiveCellAddress = useFormulaStore((s) => s.setActiveCellAddress);
  const setActiveCellFormula = useFormulaStore((s) => s.setActiveCellFormula);
  const setFormulaBarValue   = useFormulaStore((s) => s.setFormulaBarValue);
  const setEditing           = useFormulaStore((s) => s.setEditing);
  const formulaBarValue      = useFormulaStore((s) => s.formulaBarValue);
  const isEditing            = useFormulaStore((s) => s.isEditing);
  const searchText           = useUiStore((s) => s.searchText);

  const rowData = useMemo(() => {
    const src = propRowData ?? storeRowData;
    return (src || []).map((r) => ({ ...r }));
  }, [propRowData, storeRowData]);

  const columnDefs = useMemo(
    () => parseColumnsToAgDefs(sheetMeta, permissions),
    [sheetMeta, permissions],
  );

  // ── Expose API ─────────────────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    getApi:    () => gridRef.current?.api || null,
    exportCsv: (p) => gridRef.current?.api?.exportDataAsCsv(p),
  }), []);

  // ── Quick filter ──────────────────────────────────────────────────────────
  useEffect(() => {
    gridRef.current?.api?.setQuickFilter(searchText);
  }, [searchText]);

  // ── HyperFormula updates ──────────────────────────────────────────────────
  useEffect(() => {
    const unsub = formulaWorkerBridge.on('CHANGED', ({ changedCells }) => {
      const mine = changedCells.filter((c) => c.sheetIndex === hfSheetIndex);
      if (!mine.length) return;
      updateComputed(sheetId, mine, sheetMeta.columns);
      const api = gridRef.current?.api;
      if (api) {
        const nodes = mine
          .map(({ row }) => api.getDisplayedRowAtIndex(row))
          .filter(Boolean)
          .filter((v, i, a) => a.indexOf(v) === i);
        if (nodes.length) api.refreshCells({ rowNodes: nodes, force: true });
      }
    });
    return unsub;
  }, [hfSheetIndex, sheetId, sheetMeta.columns, updateComputed]);

  // ── Ctrl+S ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('spreadsheet:save', { detail: { workbookId } }));
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [workbookId]);

  // ── Mouse state tracking ──────────────────────────────────────────────────
  useEffect(() => {
    const onDown = () => { isMouseDownRef.current = true; };
    const onUp   = () => { isMouseDownRef.current = false; };
    // Use capture phase so these fire before AG Grid's own handlers
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('mouseup',   onUp,   true);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('mouseup',   onUp,   true);
    };
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const getDataRowIndex = useCallback((displayRowIndex) => {
    if (displayRowIndex == null) return null;
    const node = gridRef.current?.api?.getDisplayedRowAtIndex(displayRowIndex);
    if (!node?.data?.id) return displayRowIndex;
    const i = rowData.findIndex((r) => r.id === node.data.id);
    return i >= 0 ? i : displayRowIndex;
  }, [rowData]);

  // Builds an A1 or A1:C3 address from an AG Grid range object
  const buildRangeAddress = useCallback((range) => {
    if (!range?.startRow || !range?.endRow || !range.columns?.length) return null;

    const rowStart = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
    const rowEnd   = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);

    const colIdxs = range.columns
      .map((c) => fieldToColIndex(c.getColId(), sheetMeta.columns))
      .filter((i) => i >= 0);
    if (!colIdxs.length) return null;

    const colStart = Math.min(...colIdxs);
    const colEnd   = Math.max(...colIdxs);
    const norm     = normalizeCellRange(rowStart, colStart, rowEnd, colEnd);

    // Single cell → "A1", multi → "A1:C3"
    if (norm.startRow === norm.endRow && norm.startCol === norm.endCol) {
      return buildCellAddress(norm.startCol, norm.startRow);
    }
    return norm.address;
  }, [sheetMeta.columns]);

  // Insert a cell/range reference into the formula bar.
  // Always reads latest formula from the store (avoids stale closure).
  // Replaces the previous reference if one was inserted, otherwise appends.
  const insertFormulaReference = useCallback((newRef) => {
    const state   = useFormulaStore.getState();
    const formula = state.formulaBarValue;

    if (!newRef || !formula.startsWith('=')) return;

    const prev = prevRefRef.current;
    let   next = formula;

    if (prev && formula.includes(prev)) {
      // Replace previous reference in-place
      const idx = formula.lastIndexOf(prev);
      next = formula.slice(0, idx) + newRef + formula.slice(idx + prev.length);
    } else {
      // Append smartly
      if (formula.endsWith('(') || formula === '=') {
        next = formula + newRef;
      } else if (formula.match(/[+\-*/^,(=]$/)) {
        // After an operator or comma — append directly
        next = formula + newRef;
      } else {
        // We're mid-token or after a previous cell address — add comma separator
        next = formula + ',' + newRef;
      }
    }

    prevRefRef.current = newRef;
    state.setFormulaBarValue(next);
  }, []);

  // ── Cell editor → formula bar sync ───────────────────────────────────────
  const attachEditorSync = useCallback(() => {
    // Detach any old listener
    if (editorSyncRef.current) {
      const { el, fn } = editorSyncRef.current;
      el.removeEventListener('input', fn);
      editorSyncRef.current = null;
    }
    setTimeout(() => {
      const el = document.querySelector(
        '.ag-cell-inline-editing input, .ag-cell-inline-editing textarea'
      );
      if (!el) return;
      const fn = () => {
        const val = el.value ?? '';
        useFormulaStore.getState().setFormulaBarValue(val);
        // Enter formula mode if value starts with '='
        if (val.startsWith('=')) {
          useFormulaStore.getState().setEditing(true);
        }
      };
      el.addEventListener('input', fn);
      editorSyncRef.current = { el, fn };
    }, 0);
  }, []);

  const detachEditorSync = useCallback(() => {
    if (!editorSyncRef.current) return;
    const { el, fn } = editorSyncRef.current;
    el.removeEventListener('input', fn);
    editorSyncRef.current = null;
  }, []);

  // ── Grid event handlers ────────────────────────────────────────────────────

  const handleGridReady = useCallback((params) => {
    if (onGridReady) onGridReady(params.api);
  }, [onGridReady]);

  const handleCellFocused = useCallback(async (event) => {
    const { rowIndex, column } = event;
    if (rowIndex == null || !column) return;

    // ── ALWAYS read state fresh from store, never from closure ──
    const state = useFormulaStore.getState();
    const inFormulaMode = state.isEditing && state.formulaBarValue?.startsWith('=');

    if (inFormulaMode) {
      // In formula mode: use this cell focus to insert a reference.
      // BUT only do it for genuine single clicks (mouse is UP).
      // During a drag, the mouse is still down — skip here and let
      // onRangeSelectionChanged handle the final settled range.
      if (isMouseDownRef.current) {
        // Mid-drag: do nothing. onRangeSelectionChanged will fire after drag ends.
        return;
      }

      // Single click: build and insert the cell address
      const field      = column.getColId();
      const colIdx     = fieldToColIndex(field, sheetMeta.columns);
      const dataRowIdx = getDataRowIndex(rowIndex) ?? rowIndex;
      const address    = buildCellAddress(colIdx, dataRowIdx);
      insertFormulaReference(address);
      return; // Do NOT navigate, do NOT update formula bar, do NOT call HF
    }

    // ── Normal navigation (not in formula mode) ──────────────────────────
    prevRefRef.current = null;

    const field      = column.getColId();
    const colIdx     = fieldToColIndex(field, sheetMeta.columns);
    const dataRowIdx = getDataRowIndex(rowIndex) ?? rowIndex;
    const address    = buildCellAddress(colIdx, dataRowIdx);

    setActiveCellAddress(address);

    try {
      const result = await formulaWorkerBridge.getCell(hfSheetIndex, dataRowIdx, colIdx);
      setActiveCellFormula(result.formula || null);
      // setActiveCellFormula calls setFormulaBarValue internally (when not in formula mode)
    } catch {
      const raw = useSheetStore.getState().rowDataMap[sheetId]?.[dataRowIdx]?.[field];
      setFormulaBarValue(raw != null ? String(raw) : '');
    }
  }, [sheetId, sheetMeta.columns, hfSheetIndex, setActiveCellAddress, setActiveCellFormula,
      setFormulaBarValue, getDataRowIndex, insertFormulaReference]);

  const handleRangeSelectionChanged = useCallback(() => {
    // Only act during formula mode
    const state = useFormulaStore.getState();
    if (!state.isEditing || !state.formulaBarValue?.startsWith('=')) return;

    // Debounce: wait for drag to settle
    if (rangeDebounceRef.current) clearTimeout(rangeDebounceRef.current);

    rangeDebounceRef.current = setTimeout(() => {
      rangeDebounceRef.current = null;

      // Don't act if mouse is still down (drag not finished yet)
      if (isMouseDownRef.current) return;

      const ranges = gridRef.current?.api?.getCellRanges?.() || [];
      if (!ranges.length) return;

      const addr = buildRangeAddress(ranges[ranges.length - 1]);
      if (addr) insertFormulaReference(addr);
    }, 80);
  }, [buildRangeAddress, insertFormulaReference]);

  const handleCellEditingStarted = useCallback((event) => {
    const state = useFormulaStore.getState();

    // If the formula bar is already in formula mode, cancel the grid's editor.
    // The user is picking cells for the formula, not editing this cell.
    if (state.isEditing && state.formulaBarValue?.startsWith('=')) {
      event.api?.stopEditing(true);
      return;
    }

    setEditing(true);

    // Seed formula bar with the current cell value
    const { rowIndex, column, value } = event;
    if (column && rowIndex != null) {
      const field      = column.getColId();
      const colIdx     = fieldToColIndex(field, sheetMeta.columns);
      const dataRowIdx = getDataRowIndex(rowIndex) ?? rowIndex;
      formulaWorkerBridge.getCell(hfSheetIndex, dataRowIdx, colIdx)
        .then((r) => setFormulaBarValue(r.formula || (r.value != null ? String(r.value) : '')))
        .catch(() => setFormulaBarValue(value != null ? String(value) : ''));
    }

    attachEditorSync();
  }, [sheetMeta.columns, hfSheetIndex, getDataRowIndex, setEditing, setFormulaBarValue, attachEditorSync]);

  const handleCellEditingStopped = useCallback((event) => {
    detachEditorSync();
    setEditing(false);
    prevRefRef.current = null;

    const { rowIndex, column } = event;
    if (column && rowIndex != null) {
      const field      = column.getColId();
      const colIdx     = fieldToColIndex(field, sheetMeta.columns);
      const dataRowIdx = getDataRowIndex(rowIndex) ?? rowIndex;
      formulaWorkerBridge.getCell(hfSheetIndex, dataRowIdx, colIdx)
        .then((r) => {
          setFormulaBarValue(r.formula || (r.value != null ? String(r.value) : ''));
          setActiveCellFormula(r.formula || null);
        })
        .catch(() => {});
    }
  }, [sheetMeta.columns, hfSheetIndex, getDataRowIndex, setEditing, setFormulaBarValue,
      setActiveCellFormula, detachEditorSync]);

  const handleCellValueChanged = useCallback(async (params) => {
    const { data, colDef, newValue, rowIndex } = params;
    const field      = colDef.field;
    const colMeta    = sheetMeta.columns.find((c) => c.field === field) || {};
    const col        = fieldToColIndex(field, sheetMeta.columns);
    const dataRowIdx = getDataRowIndex(rowIndex) ?? rowIndex;

    const error = validationEngine.validateCell(newValue, colMeta, data);
    if (error) setRowErrors(sheetId, dataRowIdx, { [field]: error });
    else        setRowErrors(sheetId, dataRowIdx, {});

    updateCell(sheetId, data.id, field, newValue);

    try {
      await formulaWorkerBridge.setCell(hfSheetIndex, dataRowIdx, col, newValue);
    } catch (err) {
      console.error('[GridWrapper] HF setCell error:', err);
    }

    if (onCellChanged) onCellChanged(data.id, field, newValue);
  }, [sheetId, sheetMeta.columns, hfSheetIndex, updateCell, setRowErrors, onCellChanged, getDataRowIndex]);

  const getRowClass = useCallback((params) => {
    const dirty = useSheetStore.getState().dirtyRowsMap[sheetId] || new Set();
    return dirty.has(params.data?.id) ? 'row-dirty' : '';
  }, [sheetId]);

  const inFormulaMode = isEditing && formulaBarValue?.startsWith('=');

  return (
    <>
      <style>{`
        .epg-grid .ag-header { background: #1e3a5f !important; }
        .epg-grid .ag-header-cell-label { color: #e0e9ff !important; font-size: 11px; font-weight: 700 !important; letter-spacing: 0.4px; text-transform: uppercase; }
        .epg-grid .ag-header-cell { border-right: 1px solid #2d4f7f !important; border-left: 1px solid #2d4f7f !important; }
        .epg-grid .ag-row-even { background: #ffffff; }
        .epg-grid .ag-row-odd  { background: #f8fafc; }
        .epg-grid .ag-row:hover { background: #eff6ff !important; }
        .epg-grid .ag-cell { font-size: 12px; border-right: 1px solid #e8edf5 !important; border-left: 1px solid #e8edf5 !important; line-height: 36px; padding: 0 10px; font-family: 'DM Sans', sans-serif; }
        .epg-grid .ag-cell-inline-editing { border: 2px solid #1a56db !important; box-shadow: 0 0 0 3px rgba(26,86,219,0.12) !important; }
        .epg-grid .ag-cell-range-selected-1:not(.ag-cell-inline-editing),
        .epg-grid .ag-cell-range-selected-2:not(.ag-cell-inline-editing),
        .epg-grid .ag-cell-range-selected-3:not(.ag-cell-inline-editing),
        .epg-grid .ag-cell-range-selected-4:not(.ag-cell-inline-editing) { background-color: rgba(26,86,219,0.10) !important; }
        .epg-grid .ag-paging-panel { border-top: 1px solid #e2e8f0; background: #f8fafc; font-size: 11px; }

        /* Formula mode: crosshair cursor + yellow hover to signal "click to insert ref" */
        .epg-grid.formula-mode .ag-cell { cursor: crosshair !important; }
        .epg-grid.formula-mode .ag-cell:hover { background: #fef9c3 !important; outline: 1px solid #ca8a04; }
        .epg-grid.formula-mode .ag-row:hover { background: transparent !important; }

        .cell-formula  { background: #eef2ff !important; }
        .cell-locked   { background: #f9fafb !important; color: #9ca3af; cursor: not-allowed !important; }
        .cell-negative { color: #dc2626 !important; }
        .row-dirty     { background: #fffdf0 !important; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div
        className={`ag-theme-alpine epg-grid${inFormulaMode ? ' formula-mode' : ''}`}
        style={{ flex: 1, width: '100%', minHeight: 0, height: '100%' }}
      >
        <AgGridReact
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={rowData}
          defaultColDef={DEFAULT_COL_DEF}
          enableRangeSelection={true}
          enableRangeHandle={true}
          suppressMultiRangeSelection={false}
          getRowId={(p) => String(p.data.id)}
          getRowClass={getRowClass}
          onGridReady={handleGridReady}
          onCellValueChanged={handleCellValueChanged}
          onCellFocused={handleCellFocused}
          onRangeSelectionChanged={handleRangeSelectionChanged}
          onCellEditingStarted={handleCellEditingStarted}
          onCellEditingStopped={handleCellEditingStopped}
          style={{ height: '100%', width: '100%' }}
          {...GRID_OPTIONS}
        />
      </div>
    </>
  );
}));

export default GridWrapper;
